import React from 'react';
import { Avatar, Tag, Spin } from 'antd';
import { UserOutlined, RobotOutlined } from '@ant-design/icons';
import { useLanguage } from '../../contexts/LanguageContext';
import ChatMediaContent from './ChatMediaContent';
import { getInitial, formatTime, isImageType, isVideoType, isMixedType, isAudioType } from './utils';

/**
 * 單則訊息氣泡。
 * activeSession 提供聯絡人/AI staff 資訊以渲染頭像與名稱。
 */
const ChatMessageBubble = ({ msg, activeSession }) => {
  const { t } = useLanguage();
  const isAssistant = msg.role === 'assistant';

  const aiName =
    activeSession?.aiStaffName || t('workflowMonitor.chatBotAiStaff') || 'AI Staff';
  const aiAvatar = activeSession?.aiStaffAvatarUrl;
  const userName = activeSession?.contactName
    ? `${activeSession.contactName} (${activeSession.waId})`
    : activeSession?.waId || t('workflowMonitor.chatBotUser') || '用戶';
  const userAvatar = activeSession?.contactAvatarUrl;

  // 從訊息資料判斷是否有媒體，不能依賴 JSX 元素是否為 null（JSX 永遠是物件）
  const hasMedia = !isAssistant && !!(
    msg.messageType && (
      isImageType(msg.messageType) ||
      isVideoType(msg.messageType) ||
      isMixedType(msg.messageType) ||
      isAudioType(msg.messageType)
    )
  );
  const mediaEl = hasMedia ? <ChatMediaContent msg={msg} /> : null;
  const bubbleContent = isAssistant ? msg.content : hasMedia ? mediaEl : msg.content;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isAssistant ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 8,
        marginBottom: 10
      }}
    >
      <Avatar
        size={32}
        src={isAssistant ? aiAvatar : userAvatar}
        style={{
          backgroundColor: isAssistant ? '#1677ff' : '#25d366',
          flexShrink: 0
        }}
        icon={
          isAssistant
            ? !aiAvatar
              ? <RobotOutlined />
              : undefined
            : !userAvatar
            ? <UserOutlined />
            : undefined
        }
      >
        {isAssistant
          ? !aiAvatar
            ? getInitial(aiName)
            : null
          : !userAvatar
          ? getInitial(userName)
          : null}
      </Avatar>

      <div
        style={{
          maxWidth: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isAssistant ? 'flex-end' : 'flex-start'
        }}
      >
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2, paddingInline: 6 }}>
          {isAssistant ? aiName : userName}
          {msg.intervention && (
            <Tag
              color="orange"
              style={{ marginInlineStart: 6, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}
            >
              {t('workflowMonitor.chatBotAdminIntervention') || 'Admin'}
            </Tag>
          )}
        </div>

        <div
          style={{
            background: isAssistant ? '#dcf8c6' : '#fff',
            border: isAssistant ? 'none' : '1px solid #e8e8e8',
            borderRadius: 12,
            padding: hasMedia ? '6px 8px' : '8px 12px',
            boxShadow: '0 1px 1px rgba(0,0,0,0.06)',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            fontSize: 14,
            lineHeight: 1.5
          }}
        >
          {bubbleContent}
        </div>

        <div style={{ fontSize: 10, color: '#999', marginTop: 3, paddingInline: 6 }}>
          {formatTime(msg.at)}
          {msg.sending && (
            <span style={{ marginInlineStart: 4 }}>
              <Spin size="small" />
            </span>
          )}
          {msg.failed && (
            <span style={{ marginInlineStart: 4, color: '#ff4d4f' }}>
              {t('workflowMonitor.chatBotMessageFailed') || '發送失敗'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageBubble;
